import { parseSsrsDate } from './ssrsDate';

const MS_PER_DAY = 86_400_000;

/**
 * Turns a computed default's resolved date into the fact a reader actually needs: not what the
 * date happens to be next week, but how it moves relative to whenever the report runs.
 *
 * "Computed" already said the value is not fixed; the resolved value already said what it is right
 * now. Neither says whether the window is RIGHT — a subscription meant to cover the last 30 days
 * and one meant to cover the last 300 can both resolve to a Tuesday in July, and only the
 * relationship to today tells them apart. That is the whole reason this exists: probed live on
 * /Sales/Scope 3, FromDate and ToDate cascade from DefaultDatePeriod ("Previous Month" by default)
 * to the first and last day of last month — checked twice a few days apart (2026-06-01/06-30, then
 * 2026-07-01/07-31 once the month rolled over), which "31/07/2026 00:00:00" alone does not say.
 *
 * Deliberately arithmetic rather than linguistic. This compares the resolved value against today
 * and reports the difference — it does not, and cannot, read the report's own expression, which
 * the portal never sees. A date with no clean relationship (not today, not adjacent, not the edge
 * of a nearby month) gets a day count rather than a guess, because a day count is still an honest
 * fact about the gap and never a fabricated one.
 *
 * Time of day is ignored on purpose. The literal resolved value carries it already (and DateField
 * separately calls out a non-midnight time as a fact of its own); the relationship is about which
 * day this is, not which second.
 */
export function describeDateRelationship(rawValue: string, now: Date): string | null {
  const parsed = parseSsrsDate(rawValue);
  if (!parsed) return null;

  const [yyyy, mm, dd] = parsed.date.split('-').map(Number);
  const resolved = Date.UTC(yyyy, mm - 1, dd);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((resolved - today) / MS_PER_DAY);

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays === -1) return 'yesterday';

  // Worth naming on its own: a report whose FromDate/ToDate cascade from a "Previous Month" style
  // period — confirmed live on /Sales/Scope 3 — resolves to exactly the first or last day of some
  // month next to this one, and the raw day count ("14 days ago", said on the 15th, or "3 days
  // ago" for a month-end) would obscure the one thing that actually matters: that the window
  // follows the calendar rather than landing on an arbitrary day.
  const monthDiff = (yyyy - now.getFullYear()) * 12 + (mm - 1 - now.getMonth());
  const monthName = monthDiff === 0 ? 'this month' : monthDiff === 1 ? 'next month' : monthDiff === -1 ? 'last month' : null;

  if (monthName) {
    if (dd === 1) return `first of ${monthName}`;
    // Adding a day rolls the UTC calendar into the next month, so today is the last one this
    // month has. Reads back with getUTCMonth() because `resolved` was built from Date.UTC — a
    // local-time getter here could disagree with it by a day depending on the machine's timezone.
    if (new Date(resolved + MS_PER_DAY).getUTCMonth() !== mm - 1) return `last day of ${monthName}`;
  }

  return diffDays > 0 ? `today + ${diffDays}` : `${Math.abs(diffDays)} days ago`;
}
